// Cache buster: add daily param to bypass Chrome ERR_CACHE_READ_FAILURE on Supabase storage.
// opts.bust=false → URL stabil (boleh di-cache browser+CF lama). Dipakai gambar produk LP
// (LCP) — URL ganti tiap hari = cache MISS terus. Default true = perilaku lama.
const CACHE_BUSTER = new Date().toISOString().slice(0, 10).replace(/-/g, '');
export const getImageUrl = (url?: string | null, opts?: { bust?: boolean }): string => {
  if (!url) return '';
  const normalized = url.replace('images.cuanmax.digital', 'ggxyaautsdukyapstlgr.supabase.co');
  if (!normalized.includes('supabase.co/storage')) return normalized;
  if (normalized.includes('render/image')) return normalized;
  const renderUrl = normalized.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
  const sep = renderUrl.includes('?') ? '&' : '?';
  const base = `${renderUrl}${sep}width=800&quality=80&format=webp&resize=contain`;
  return opts?.bust === false ? base : `${base}&cb=${CACHE_BUSTER}`;
};

/**
 * Transform Supabase storage object URLs to render endpoint with image optimization.
 * Converts /storage/v1/object/public/ → /storage/v1/render/image/public/
 * with width, quality, and format params for smaller payload.
 * Falls back to original URL for non-Supabase or already-transformed URLs.
 */
export function getOptimizedImageUrl(
  url: string | null | undefined,
  options?: { width?: number; quality?: number; format?: string }
): string | null | undefined {
  if (!url) return url;
  if (!url.includes('supabase.co/storage/v1/object/public/')) return url;
  let renderUrl = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
  const w = options?.width ?? 800;
  const q = options?.quality ?? 80;
  const fmt = options?.format ?? 'webp';
  const sep = renderUrl.includes('?') ? '&' : '?';
  return `${renderUrl}${sep}width=${w}&quality=${q}&format=${fmt}&resize=contain`;
}

// Rewrite <img src> Supabase di HTML editor (deskripsi produk) → render/image
// w800/q80/webp + loading lazy + decoding async + CLS-guard (content-visibility
// + intrinsic-size agar ruang dicadangkan sebelum gambar decode). Non-Supabase dibiarkan.
const CLS_GUARD = 'content-visibility:auto;contain-intrinsic-size:auto 220px;width:100%;height:auto';
export function optimizeHtmlImages(html: string): string {
    if (!html || !html.includes('<img')) return html;
    return html.replace(
        /<img\b([^>]*?)\bsrc=(["'])(https?:[^"']+)\2/gi,
        (full, attrs: string, q: string, src: string) => {
            if (!src.includes('supabase.co/storage/v1/object/public/')) return full;
            const opt = getOptimizedImageUrl(src);
            if (!opt || opt === src) return full;
            const extra = /loading=/.test(attrs) ? '' : ' loading="lazy"';
            const extra2 = /decoding=/.test(attrs) ? '' : ' decoding="async"';
            if (/content-visibility/.test(attrs)) return `<img${attrs}src=${q}${opt}${q}${extra}${extra2}`;
            const m = attrs.match(/style=(["'])(.*?)\1/i);
            const attr2 = m ? attrs.replace(m[0], `style=${m[1]}${m[2].replace(/;?$/, ';')}${CLS_GUARD}${m[1]}`) : `${attrs} style="${CLS_GUARD}"`;
            return `<img${attr2}src=${q}${opt}${q}${extra}${extra2}`;
        }
    );
}

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const MAX = 1200;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else { width = Math.round(width * MAX / height); height = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = reject;
    img.src = objectUrl;
  });
};
